import {DeepPartial} from 'react-hook-form';

import {indexBy, isNil, isNotNil} from 'ramda';

import {useGetNoteQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {RentalOrderResponseBody} from '@dms/api/rental';
import {useGetVehicleMileageQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {RentalVehicleResponseBody, useGetRentalPriceGroupQuery} from '@dms/api/vehicle';
import {
  getNaturalPersonFullName,
  useCodeList,
  useDocumentTemplates,
  useTenant,
  useVatRatesOptions,
} from '@dms/shared';

import {Nullish, useRequiredParams} from 'shared/web';

import {RentalOrderNodeContext} from '../../../../../constants/RentalOrderNodeContext';
import {calculateCommonPrice} from '../../../utils/calculateCommonPrice';
import {RentVehicleFormValues} from '../types/RentVehicleFormValues';
import {calculateRentPrice} from '../utils/calculateRentPrice';
import {convertToCommonAddress} from '../utils/convertToCommonAddress';

export const useGetRentVehicleFormDefaultValues = (
  rentalOrder: RentalOrderResponseBody | Nullish,
  rentalVehicle: RentalVehicleResponseBody | Nullish
) => {
  const {tenant, tenantPhoneInfo} = useTenant();
  const {id: rentalOrderId} = useRequiredParams();
  const hasOrderRentVehicle = isNotNil(rentalOrder?.rentVehicle);
  const [getTemplates, {isLoading: isLoadingTemplates}] = useDocumentTemplates();

  const {primaryTemplate} = getTemplates('workshop-rental_order_contract');

  const {data: vehicleMileage, isLoading: isLoadingVehicleMileage} = useGetVehicleMileageQuery(
    {
      vehicleId: rentalOrder?.rentalVehicleId ?? '',
    },
    {
      skip: isNil(rentalOrder?.rentalVehicleId),
    }
  );

  const {data: customerData, isLoading: isLoadingCustomer} = useGetCustomerV2Query(
    {customerId: rentalOrder?.customerId ?? ''},
    {
      skip: isNil(rentalOrder?.customerId),
    }
  );

  const {data: defaultPriceGroup, isLoading: isLoadingDefaultPriceGroup} =
    useGetRentalPriceGroupQuery(
      {rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId ?? ''},
      {
        skip: isNil(rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId),
      }
    );

  const {data: rentalOrderNote, isLoading: isLoadingRentalOrderNote} = useGetNoteQuery({
    recordId: rentalOrderId,
    resourceId: EntityResourceIds.rentalOrder,
    context: RentalOrderNodeContext.rentVehicleNote,
  });
  const {data: vehicleConditionNote, isLoading: isLoadingVehicleConditionNote} = useGetNoteQuery({
    recordId: rentalOrderId,
    resourceId: EntityResourceIds.rentalOrder,
    context: RentalOrderNodeContext.rentVehicleConditionNote,
  });

  const [vatRates] = useVatRatesOptions();

  const [rentTypes, {isLoading: isLoadingRentTypes}] = useCodeList('rentType');

  const isLoading =
    isLoadingCustomer ||
    isLoadingRentTypes ||
    isLoadingRentalOrderNote ||
    isLoadingDefaultPriceGroup ||
    isLoadingVehicleConditionNote ||
    isLoadingTemplates ||
    isLoadingVehicleMileage;

  const personData =
    customerData?.discriminator === 'NATURAL_PERSON'
      ? customerData?.foundingPerson
      : customerData?.contacts?.[0];

  const identityCardsByType = indexBy(
    (item) => item.type ?? item.id,
    personData?.identityCards ?? []
  );

  const activePriceGroupItem = defaultPriceGroup?.items.find(
    (item) =>
      item.rentalPriceGroupItemId === rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId
  );

  const {dailyRate, paidDays, freeDailyKilometers, totalFreeKilometers} = calculateRentPrice(
    activePriceGroupItem,
    rentalOrder
  );

  const rentVehicle = rentalOrder?.rentVehicle;
  const rentPrice = rentVehicle?.rentPrice;

  const rentalOrderRentValues: DeepPartial<RentVehicleFormValues> | null = isNotNil(rentVehicle)
    ? {
        authorizedPersons: rentVehicle.authorizedPersons,
        actualMileage: rentVehicle.actualMileage,
        fuelTank: parseFloat(rentVehicle.fuelTank ?? '0.5'),
        isAddressSame: isNil(rentVehicle.returnAddress),
        rentAddress: rentVehicle.rentAddress,
        rentType: rentVehicle.rentType,
        rentalOrderNote: rentalOrderNote?.text,
        conditionNote: vehicleConditionNote?.text,
        equipment: rentVehicle.equipment,
        usedPriceGroup: {
          rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId,
          rentalPriceGroupItemId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId,
        },
        isCustomPriceEnabled: isNotNil(rentVehicle.customRentPrice),
        rentPrice: {
          dailyRate: parseFloat(rentPrice?.dailyRate ?? '0'),
          paidDays: rentPrice?.paidDays,
          freeDailyKilometers: rentPrice?.freeDailyKilometers,
          totalFreeKilometers: rentPrice?.totalFreeKilometers,
          rentalPrice: {
            vatType: rentPrice?.rentalPrice?.vatType ?? 'S',
            withoutVat: parseFloat(rentPrice?.rentalPrice?.withoutVat ?? '0'),
            vat: parseFloat(rentPrice?.rentalPrice?.vat ?? '0'),
            withVat: parseFloat(rentPrice?.rentalPrice?.withVat ?? '0'),
          },
          pricePerKilometer: {
            vatType: rentPrice?.pricePerKilometer?.vatType ?? 'S',
            withoutVat: parseFloat(rentPrice?.pricePerKilometer?.withoutVat ?? '0'),
            vat: parseFloat(rentPrice?.pricePerKilometer?.vat ?? '0'),
            withVat: parseFloat(rentPrice?.pricePerKilometer?.withVat ?? '0'),
          },
          deliveryPrice: {
            vatType: rentPrice?.deliveryPrice?.vatType ?? 'S',
            withoutVat: parseFloat(rentPrice?.deliveryPrice?.withoutVat ?? '0'),
            vat: parseFloat(rentPrice?.deliveryPrice?.vat ?? '0'),
            withVat: parseFloat(rentPrice?.deliveryPrice?.withVat ?? '0'),
          },
          otherPrice: {
            vatType: rentPrice?.otherPrice?.vatType ?? 'S',
            withoutVat: parseFloat(rentPrice?.otherPrice?.withoutVat ?? '0'),
            vat: parseFloat(rentPrice?.otherPrice?.vat ?? '0'),
            withVat: parseFloat(rentPrice?.otherPrice?.withVat ?? '0'),
          },
          discount: {
            percent: parseFloat(rentPrice?.discount?.percent ?? '0'),
            discount: {
              vatType: rentPrice?.discount?.discount?.vatType ?? 'S',
              withoutVat: parseFloat(rentPrice?.discount?.discount?.withoutVat ?? '0'),
              vat: parseFloat(rentPrice?.discount?.discount?.vat ?? '0'),
              withVat: parseFloat(rentPrice?.discount?.discount?.withVat ?? '0'),
            },
          },
        },
        customPrice: {
          vatType: rentVehicle.customRentPrice?.vatType ?? 'S',
          withoutVat: parseFloat(rentVehicle.customRentPrice?.withoutVat ?? '0'),
          vat: parseFloat(rentVehicle.customRentPrice?.vat ?? '0'),
          withVat: parseFloat(rentVehicle.customRentPrice?.withVat ?? '0'),
        },
        returnAddress: rentVehicle.returnAddress,
        templateId: primaryTemplate?.id,
      }
    : null;

  const newFormDefaultValues: DeepPartial<RentVehicleFormValues> = {
    authorizedPersons: [
      {
        name: getNaturalPersonFullName(personData) || undefined,
        drivingLicenceNumber: identityCardsByType?.ID_DRIVER_LICENSE?.cardNumber ?? null,
        idCardNumber: identityCardsByType?.ID_IDENTITY_CARD?.cardNumber ?? null,
        phoneNumber: personData?.phoneNumbers?.[0] ?? {
          countryCode: tenantPhoneInfo?.countryCode,
          prefix: tenantPhoneInfo?.prefix,
        },
      },
    ],
    isAddressSame: true,
    usedPriceGroup: {
      rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId,
      rentalPriceGroupItemId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId,
    },
    rentAddress: convertToCommonAddress(tenant?.contactInformation?.address),
    actualMileage: vehicleMileage?.mileage ?? undefined,
    fuelTank: 1,
    isCustomPriceEnabled: false,
    equipment: rentalVehicle?.equipment ?? [],
    rentType: rentTypes?.[0]?.codeId,
    rentalOrderNote: rentalOrderNote?.text,
    conditionNote: vehicleConditionNote?.text,
    templateId: primaryTemplate?.id,
    rentPrice: {
      dailyRate,
      paidDays,
      freeDailyKilometers,
      totalFreeKilometers,
      rentalPrice: calculateCommonPrice((dailyRate ?? 0) * (paidDays ?? 0), vatRates),
      pricePerKilometer: calculateCommonPrice(
        parseFloat(activePriceGroupItem?.price?.ratePerKilometer ?? '0'),
        vatRates
      ),
      discount: {discount: {vatType: 'S', vat: 0, withoutVat: 0, withVat: 0}, percent: 0},
      deliveryPrice: {vatType: 'S', vat: 0, withoutVat: 0, withVat: 0},
      otherPrice: {vatType: 'S', vat: 0, withoutVat: 0, withVat: 0},
    },
    customPrice: {
      vatType: 'S',
    },
  };

  const defaultValues = hasOrderRentVehicle ? rentalOrderRentValues : newFormDefaultValues;

  return [defaultValues, isLoading] as const;
};
